import { v4 as uuid } from "uuid";
import { z } from "zod";
import { PolicyCategory, policyCategoryIsForm, PolicyFrequency, PolicyType, RegisterSubject } from "../../entities/projectPolicy";
import { ProjectCommmitmentGenerationSource } from "../project/enums";
export const projectPolicyLineItemSchema = z.object({
    created: z.date(),
    frequency: z.enum(PolicyFrequency),
    generationSource: z.enum(ProjectCommmitmentGenerationSource).optional(),
    id: z.string(),
    /** True once the policy carries at least one question — see the entity. */
    isForm: z.boolean(),
    isProjectLevelPolicy: z.boolean(),
    policyCategory: z.enum(PolicyCategory),
    policyDescription: z.string().optional(),
    policyType: z.enum(PolicyType),
    projectId: z.string(),
    questions: z.array(z.custom()),
    /** Registers only — see `RegisterSubject`. */
    registerSubject: z.enum(RegisterSubject).optional(),
    /**
     * A register a credit generated, not yet profiled: its frequency, level and
     * Yes / No question are still the user's to set (#113). UI only — never
     * encoded, so the wizard refuses to save while any policy carries it.
     */
    requiresSetup: z.boolean().optional(),
    sourceFrameworkId: z.string().optional(),
    sourceFrameworkTargetId: z.string().optional(),
    uri: z.string().optional()
});
export const policyTypeToString = (socialCommitmentType) => {
    switch (socialCommitmentType) {
        case PolicyType.DefaultPolicy:
            return "Default Policy";
        case PolicyType.FamilyViolencePolicy:
            return "Family Violence Leave Policy";
        case PolicyType.EnvironmentalSustainabilityPolicy:
            return "Environmental Sustainability Policy";
        case PolicyType.EntityLevelGenderEqualityActionPlan:
            return "Organisational Level Gender Equality Action Plan";
        case PolicyType.ProjectLevelGenderEqualityActionPlan:
            return "Project Level Gender Equality Action Plan";
        case PolicyType.FairJobsCode:
            return "Fair Jobs Code";
    }
};
export const policyTypeToDescription = (socialCommitmentType) => {
    switch (socialCommitmentType) {
        case PolicyType.DefaultPolicy:
            return "Default Policy";
        case PolicyType.FamilyViolencePolicy:
            return `An entity's business-wide policy for entitlements offered to employees who are experiencing family and domestic violence to deal with the impacts of this violence where it is not practical to do so outside their work hours.

      This might include making arrangements for their own or a family member's safety (including relocation), attending court or accessing police services, or attending counselling, or appointments with medical, financial or legal professionals.

      Family and domestic violence refers to violent, threatening or other abusive behaviour that seeks to coerce or control the employee and causes them harm or fear.`;
        case PolicyType.EnvironmentalSustainabilityPolicy:
            return `An entity's business-wide policy being a commitment to stakeholders around complying with legal obligations, minimising pollution, and applying a systematic environmental management approach that is consistent with the international standard ISO 14001. A robust policy will address the following environmental management issues:
      Energy use
      Greenhouse gas emissions and ozone depleting substances
      Suppliers, produces and materials use
      Office waste, building waste, and resource recovery
      Potable water use and waste water`;
        case PolicyType.EntityLevelGenderEqualityActionPlan:
            return "An organisation's business-wide policy to plan, implement and measure change in order to achieve gender equality in the workplace. The policy must include results of a workplace gender audit to assess the current state of gender equality in the workplace, and strategies and measures to improve gender equality in the workplace, based on the results of the audit.";
        case PolicyType.ProjectLevelGenderEqualityActionPlan:
            return `A document outlining strategies to to support gender equality at the project level by:
      Understanding the importance of gender equality
      Identifying where change is needed
      Planning and implementing actions to achieve progress
      Measuring change over time.

      Different strategies apply at the project level compared to the organisational level, i.e. the gender pay gap of an organisation and a project will differ and will require different targets to eliminate or close the gap.`;
        case PolicyType.FairJobsCode:
            return "A code introduced by the Victorian Government designed to promote labour standards and encourage compliance with employment, industrial relations and workplace health and safety obligations.";
    }
};
export const policyTypeToStringShort = (socialCommitmentType) => {
    switch (socialCommitmentType) {
        case PolicyType.DefaultPolicy:
            return "Default Policy";
        case PolicyType.FamilyViolencePolicy:
            return "FVP";
        case PolicyType.EnvironmentalSustainabilityPolicy:
            return "ESP";
        case PolicyType.EntityLevelGenderEqualityActionPlan:
            return "ELGEAP";
        case PolicyType.ProjectLevelGenderEqualityActionPlan:
            return "PLGEAP";
        case PolicyType.FairJobsCode:
            return "FJC";
    }
};
export const createDefaultProjectPolicy = (projectId) => projectPolicyLineItemSchema.parse({
    created: new Date(),
    frequency: PolicyFrequency.OneTime,
    generationSource: ProjectCommmitmentGenerationSource.Custom,
    id: uuid(),
    isForm: false,
    isProjectLevelPolicy: false,
    policyCategory: PolicyCategory.Document,
    policyType: PolicyType.DefaultPolicy,
    projectId,
    questions: []
});
export const policyCategoryToString = (category) => {
    switch (category) {
        case PolicyCategory.Document:
            return "Document";
        case PolicyCategory.Form:
            return "Form";
        case PolicyCategory.Survey:
            return "Survey";
        case PolicyCategory.Register:
            return "Register";
    }
};
/**
 * The question rules a policy's category holds its questions to — the options
 * `validatePolicyQuestions` takes. One place, so the builder and the backend
 * cannot disagree about what a register may ask.
 */
export const questionRulesForCategory = (category) => ({
    requireAtLeastOne: policyCategoryIsForm(category),
    singleQuestion: category === PolicyCategory.Register
});
/** A policy's Type as tables show it: "Register (Employees)" for a register. */
export const policyCategoryLabel = (policy) => policy.registerSubject
    ? `${policyCategoryToString(policy.policyCategory)} (${registerSubjectToString(policy.registerSubject)})`
    : policyCategoryToString(policy.policyCategory);
/** The "Register for" options, as the setup drawer and the documents table read them. */
export const registerSubjectToString = (subject) => {
    switch (subject) {
        case RegisterSubject.Employee:
            return "Employees";
        case RegisterSubject.Package:
            return "Packages";
    }
};
export const projectLevelPolicies = [
    PolicyType.FairJobsCode,
    PolicyType.ProjectLevelGenderEqualityActionPlan,
    PolicyType.EntityLevelGenderEqualityActionPlan
];
